/**
 * TanStack Query hooks for the Security board (Proofpoint TAP).
 *
 * The backend serves these routes from stored daily rollups, never from TAP
 * itself, so a page load costs zero requests against TAP's budget. That is not
 * an optimisation: the SIEM endpoints share a per-credential ceiling per rolling
 * 24h, and a 429 clears only as the oldest request ages out of that window — so
 * a handful of open dashboards fetching on demand could lock the credential out
 * for hours. The refetch intervals below therefore track the backend's own
 * five-minute collector rather than trying to be fresher than it.
 *
 * SDK functions are imported from `@/lib/api/generated` rather than the
 * `@/lib/api` barrel, matching the other hooks files: the barrel re-exports
 * these hooks, so importing it here would close an import cycle.
 */

import { queryOptions, useQuery } from "@tanstack/react-query";

import {
  getSecurityCampaigns,
  getSecurityHistory,
  getSecurityOverview,
  getSecurityTargetedPeople,
  type SecurityOverview,
  type SecurityTargetedPeople,
} from "@/lib/api/generated";
import { logger } from "@/lib/logger";

/** Query key for the board's main payload. */
export const SECURITY_OVERVIEW_KEY = ["security", "overview"] as const;

/** Query key for the role-gated identity payload. */
export const SECURITY_TARGETED_PEOPLE_KEY = ["security", "targeted-people"] as const;

/** Query key for the campaign list. */
export const SECURITY_CAMPAIGNS_KEY = ["security", "campaigns"] as const;

/** Query key factory for the explicit-window history route. */
export function securityHistoryKey(days: number) {
  return ["security", "history", days] as const;
}

/**
 * How often the board refetches, in ms. The backend polls TAP every 300s and
 * caches the built snapshot for 60s, so anything faster re-reads the same rows.
 */
const REFETCH_INTERVAL_MS = 60_000;

/** Default trend window, matching the backend's reporting window. */
export const SECURITY_TREND_DAYS = 90;

/**
 * The Security board: the executive figures, the trends behind them, the
 * classification mix, campaigns, mail-flow volume and collector health.
 *
 * Aggregates only. The named identity list lives on its own route
 * (`useSecurityTargetedPeople`) because it is role-gated and must not be cached
 * or persisted alongside the aggregates.
 */
export function useSecurityOverview() {
  return useQuery(securityOverviewQuery());
}

/**
 * The board's entry query, as options rather than a hook — see
 * `networkOverviewQuery` for why. Prefetching this costs TAP nothing: the route
 * is served from stored rollups, never from TAP itself.
 */
export function securityOverviewQuery() {
  return queryOptions({
    queryKey: SECURITY_OVERVIEW_KEY,
    queryFn: async () => {
      const { data } = await getSecurityOverview();
      // Counts and status only. Never spread the payload into a log line —
      // that habit is how identity leaks into a console once a schema grows.
      logger.debug("[security] overview received", {
        configured: data?.configured,
        lastStatus: data?.health?.last_status,
        circuitOpen: data?.health?.budget?.circuit_open,
        daysWithData: data?.summary?.days_with_data,
      });
      return data;
    },
    refetchInterval: REFETCH_INTERVAL_MS,
  });
}

/**
 * The named Very Attacked People, or the counts alone without the role.
 *
 * The route never 403s: a non-admin session receives the same aggregate counts
 * with `admin: false`, an empty `people` list and a `withheld_reason`. So this
 * is one query for both audiences, and the board renders the state it is given.
 *
 * `gcTime` is deliberately short. Names are the one thing on this board that
 * should not linger in a cache after the panel that showed them is gone — the
 * same treatment `useReclaimableAccounts` gives licence identities.
 */
export function useSecurityTargetedPeople(enabled = true) {
  return useQuery({
    queryKey: SECURITY_TARGETED_PEOPLE_KEY,
    queryFn: async () => {
      const { data } = await getSecurityTargetedPeople();
      logger.debug("[security] targeted people received", {
        admin: data?.admin,
        vapCount: data?.vap_count,
        named: data?.people?.length ?? 0,
      });
      return data;
    },
    // Names move on the once-daily People batch, not the five-minute poll.
    refetchInterval: 5 * REFETCH_INTERVAL_MS,
    gcTime: 2 * 60_000,
    enabled,
  });
}

/**
 * The attributed campaigns behind the board's attribution line.
 *
 * The overview already carries the campaigns it renders; this is the wider list
 * for a drill-down that wants the whole window.
 *
 * @param enabled Skip the request while TAP is unconfigured — the overview
 *   already carries a known-empty answer.
 */
export function useSecurityCampaigns(enabled = true) {
  return useQuery({
    queryKey: SECURITY_CAMPAIGNS_KEY,
    queryFn: async () => {
      const { data } = await getSecurityCampaigns();
      logger.debug("[security] campaigns received", {
        count: data?.campaigns?.length ?? 0,
        actors: data?.actors?.length ?? 0,
      });
      return data;
    },
    refetchInterval: 5 * REFETCH_INTERVAL_MS,
    enabled,
  });
}

/**
 * The per-day series over an explicit window.
 *
 * @param days Window length. The backend rejects anything past its retention,
 *   since older rows are deleted and would render as real zeros.
 * @param enabled Skip the request while TAP is unconfigured — the answer is a
 *   known-empty payload the overview already carries.
 */
export function useSecurityHistory(days = SECURITY_TREND_DAYS, enabled = true) {
  return useQuery({
    queryKey: securityHistoryKey(days),
    queryFn: async () => {
      const { data } = await getSecurityHistory({ query: { days } });
      logger.debug("[security] history received", {
        days,
        points: data?.trend?.length ?? 0,
      });
      return data;
    },
    // Daily rollups only shift as poll cycles land.
    refetchInterval: 5 * REFETCH_INTERVAL_MS,
    enabled,
  });
}

/**
 * What the Security board reads: the aggregates, plus the identity payload.
 *
 * Two queries rather than one is deliberate here, and it is not the leftover of
 * the two boards that merged. The aggregates are the same for everyone and are
 * cheap to hold; the names are role-gated, short-lived in cache and never
 * persisted. Keeping them in separate queries is what makes those two different
 * lifetimes expressible at all.
 */
export interface SecurityBoardData {
  /** No aggregate payload yet. */
  isPending: boolean;
  /** The first error from either route, or null. */
  error: Error | null;
  /** A background refetch is in flight — hold the frame, don't flash a skeleton. */
  isFetching: boolean;
  /** The board's aggregates. */
  overview: SecurityOverview | undefined;
  /** Counts for everyone; names for the security role. */
  targeted: SecurityTargetedPeople | undefined;
}

export function useSecurityBoard(): SecurityBoardData {
  const overview = useQuery(securityOverviewQuery());
  const configured = overview.data?.configured ?? false;
  const targeted = useSecurityTargetedPeople(configured);

  return {
    isPending: overview.isPending,
    error: (overview.error ?? targeted.error) as Error | null,
    isFetching: overview.isFetching || targeted.isFetching,
    overview: overview.data,
    targeted: targeted.data,
  };
}
